import { Box, Button, CircularProgress, Grid2, Tooltip } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { viewImage } from "../../utils/viewPDF";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoIcon from "@mui/icons-material/Info";
import { uploadFile } from "../../utils/uploadToAzure";

const OrgDetails = () => {
  const [org, setOrg] = useState();
  const [errors, setErrors] = useState("");
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [fileNames, setFileNames] = useState({ orgLogo: "" });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const getOrg = async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${storedUser.username}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setOrg(res.data);
      if (res.data.orgLogo && res.data.orgUsername && res.data.orgShortform) {
        navigate(`/admin`);
      }
    };
    getOrg();
  }, []);

  const handleChange = (e) => {
    const updatedValue = e.target.name === "orgShortform" ? e.target.value.toUpperCase() : e.target.value;
    setOrg({ ...org, [e.target.name]: updatedValue });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = org || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.orgLogo)) newErrors.orgLogo = "Org Logo is required";
    if (isEmpty(data.orgUsername))
      newErrors.orgUsername = "Org Username is required";
    if (isEmpty(data.orgShortform)) {
      newErrors.orgShortform = "Org Shortform is required";
    } else if (!/^[A-Z0-9]{3}$/.test(data.orgShortform)) {
      newErrors.orgShortform =
        "Must be exactly 3 uppercase characters with no special characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;
    setUploading(true);
    if (files && files.length > 0) {
      const file = files[0];

      // Validate image dimensions
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);
      image.src = objectUrl;

      image.onload = async () => {
        const { width, height } = image;

        URL.revokeObjectURL(objectUrl); // Cleanup

        if (width < 400 || width > 600 || height < 400 || height > 600) {
          setErrors((prev) => ({
            ...prev,
            [name]:
              "Image dimensions must be between 400 and 600 pixels (both width and height)",
          }));
          setUploading(false)
          return;
        }

        // Continue with upload
        setFileNames((prev) => ({
          ...prev,
          [name]: file.name,
        }));

        const formData = new FormData();
        formData.append("file", file);

        try {
          const res = await uploadFile(file, org.orgId, null, null, storedUser)
          setOrg({ ...org, orgLogo: res });
          setErrors({ ...errors, orgLogo: "" });
        } catch (error) {
          console.error("File upload error:", error);
        } finally {
          setUploading(false);
        }
      };

      image.onerror = () => {
        setErrors((prev) => ({
          ...prev,
          [name]: "Invalid image file",
        }));
      };
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/organization?orgId=${org.orgId}`,
        {
          orgLogo: org.orgLogo,
          orgUsername: org.orgUsername,
          orgShortform: org.orgShortform,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      navigate(`/admin`);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin`);
  };
  return (
    <div style={{ position: "relative" }}>
      {loading && <div className="loading-div"></div>}
      <div className="dashboard-container">
        <h2>Enter Organization Details</h2>
        <Grid2
          container
          columnSpacing={2}
          sx={{ width: "60%", margin: "auto" }}
          py={5}
        >
          <Grid2 size={{ xs: 12, sm: 6 }}>
            {/* //512x512 */}
            <label htmlFor="orgLogo">
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <div>
                  Organization Logo<span style={{color: '#FF0000'}}>*</span>
                </div>
                <Tooltip title="Please upload logo between 400 and 600 pixels">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div className={`input-file ${errors.orgLogo && "input-error"}`}>
                <input
                  type="file"
                  id="orgLogo"
                  name="orgLogo"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.orgLogo || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {uploading && <CircularProgress />}
            {errors.orgLogo && <p className="error">{errors.orgLogo}</p>}
            {org && org.orgLogo && (
              <Button
                sx={{
                  margin: "-10px 0 10px 0 !important",
                  padding: "0 !important",
                  width: "max-content !important",
                  fontSize: "12px !important",
                  textDecoration: "underline",
                  textAlign: "start",
                }}
                onClick={() => viewImage(org.orgLogo, storedUser)}
              >
                {fileNames.orgLogo}
              </Button>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <label htmlFor="orgUsername">
              Organization Username (in smallcase)<span style={{color: '#FF0000'}}>*</span>
            </label>
            <input
              type="text"
              id="orgUsername"
              name="orgUsername"
              onChange={handleChange}
              value={org && org.orgUsername}
              className={`${errors.orgUsername && "input-error"}`}
            />
            {errors.orgUsername && (
              <p className="error">{errors.orgUsername}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <label
              htmlFor="orgShortform"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                Organization Shortform (3 letters)<span style={{color: '#FF0000'}}>*</span>
              </div>
              <Tooltip title="We use your 3 letter shortform in your tender ID e.g. ARG_A_2025_654321">
                <InfoIcon fontSize="small" />
              </Tooltip>
            </label>
            <input
              type="text"
              id="orgShortform"
              name="orgShortform"
              onChange={handleChange}
              value={org && org.orgShortform}
              className={`${errors.orgShortform && "input-error"}`}
              maxLength={3}
            />
            {errors.orgShortform && (
              <p className="error">{errors.orgShortform}</p>
            )}
          </Grid2>
        </Grid2>
      </div>
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={handleUpdate}>
          Submit
        </Button>
      </Box>
    </div>
  );
};

export default OrgDetails;